import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { adminApi } from "../../api/adminApi";
import type { BrandingDto } from "../../api/types";

const DEFAULTS = { primaryColor: "#4f46e5", secondaryColor: "#14b8a6", accentColor: "#161a2b" };
const MAX_LOGO_BYTES = 300 * 1024;

function ColorField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="form-row">
      <label htmlFor={id}>{label}</label>
      <div className="color-field">
        <label className="color-swatch" style={{ background: value }}>
          <input
            id={id}
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
          />
        </label>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="color-hex-input"
          spellCheck={false}
        />
      </div>
    </div>
  );
}

export default function Branding() {
  const [branding, setBranding] = useState<BrandingDto | null>(null);
  const [primaryColor, setPrimaryColor] = useState(DEFAULTS.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULTS.secondaryColor);
  const [accentColor, setAccentColor] = useState(DEFAULTS.accentColor);
  const [logoDataUri, setLogoDataUri] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [readingLogo, setReadingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    adminApi.branding
      .get()
      .then((b) => {
        setBranding(b);
        setPrimaryColor(b.primaryColor || DEFAULTS.primaryColor);
        setSecondaryColor(b.secondaryColor || DEFAULTS.secondaryColor);
        setAccentColor(b.accentColor || DEFAULTS.accentColor);
        setLogoDataUri(b.logoDataUri || null);
        setCompanyName(b.companyName || "");
        setEmail(b.email || "");
        setPhone(b.phone || "");
        setAddress(b.address || "");
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load branding"))
      .finally(() => setLoading(false));
  }, []);

  function handleLogoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      setError(`Logo is too large (${Math.round(file.size / 1024)}KB) — keep it under 300KB.`);
      return;
    }
    setError(null);
    setReadingLogo(true);
    const reader = new FileReader();
    reader.onload = () => {
      setLogoDataUri(reader.result as string);
      setReadingLogo(false);
    };
    reader.onerror = () => {
      setError("Failed to read that file — try a different image.");
      setReadingLogo(false);
    };
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    if (readingLogo) {
      setError("Still reading the logo file — try Save again in a moment.");
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await adminApi.branding.save({
        primaryColor,
        secondaryColor,
        accentColor,
        logoDataUri,
        companyName: companyName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        address: address.trim() || null,
      });
      setBranding(result);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save branding");
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setPrimaryColor(DEFAULTS.primaryColor);
    setSecondaryColor(DEFAULTS.secondaryColor);
    setAccentColor(DEFAULTS.accentColor);
    setLogoDataUri(null);
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <div className="page-header">
        <h1>Branding</h1>
      </div>
      <p className="help-text" style={{ marginBottom: 16, maxWidth: 640 }}>
        These colors, logo, and company details are available to every report, header, and footer
        template as <code>{"{{theme.primaryColor}}"}</code>, <code>{"{{theme.secondaryColor}}"}</code>,{" "}
        <code>{"{{theme.accentColor}}"}</code>, <code>{"{{theme.logoUrl}}"}</code>,{" "}
        <code>{"{{theme.companyName}}"}</code>, <code>{"{{theme.email}}"}</code>,{" "}
        <code>{"{{theme.phone}}"}</code> and <code>{"{{theme.address}}"}</code>.
      </p>

      {error && <div className="error-text">{error}</div>}
      {saved && <div className="success-text">Branding saved.</div>}

      <div className="branding-layout">
        <div className="page-panel">
          <h3 style={{ marginTop: 0 }}>Brand colors</h3>
          <div className="branding-color-grid">
            <ColorField id="primaryColor" label="Primary" value={primaryColor} onChange={setPrimaryColor} />
            <ColorField id="secondaryColor" label="Secondary" value={secondaryColor} onChange={setSecondaryColor} />
            <ColorField id="accentColor" label="Accent / text" value={accentColor} onChange={setAccentColor} />
          </div>

          <h3>Logo</h3>
          <div className="logo-upload">
            {logoDataUri ? (
              <div className="logo-upload-preview">
                <img src={logoDataUri} alt="Logo preview" />
                <div className="logo-upload-actions">
                  <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()}>
                    Replace
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setLogoDataUri(null)}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="logo-upload-dropzone" onClick={() => fileInputRef.current?.click()}>
                <span className="logo-upload-icon">＋</span>
                <span>Upload a logo</span>
                <span className="help-text">PNG or SVG, up to 300KB</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              style={{ display: "none" }}
            />
          </div>

          <h3>Company details</h3>
          <p className="help-text">
            Used in the header/footer of the starter report templates and available to any custom
            template you write.
          </p>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="companyName">Company name</label>
              <input id="companyName" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </div>
            <div className="form-row">
              <label htmlFor="companyEmail">Email</label>
              <input id="companyEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="form-row">
              <label htmlFor="companyPhone">Phone</label>
              <input id="companyPhone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="form-row">
              <label htmlFor="companyAddress">Address</label>
              <input id="companyAddress" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving || readingLogo}>
              {saving ? "Saving..." : readingLogo ? "Reading logo..." : "Save branding"}
            </button>
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Reset to defaults
            </button>
          </div>
        </div>

        <div className="branding-preview-panel">
          <div className="help-text" style={{ marginBottom: 10, fontWeight: 600, textTransform: "uppercase", fontSize: 11, letterSpacing: "0.05em" }}>
            Live preview
          </div>
          <div className="branding-preview-card">
            <div className="branding-preview-header" style={{ background: primaryColor }}>
              {logoDataUri ? (
                <img src={logoDataUri} alt="Logo" />
              ) : (
                <div className="branding-preview-logo-placeholder" style={{ borderColor: "rgba(255,255,255,0.5)" }} />
              )}
              <div>
                <div className="branding-preview-title">Member Statement</div>
                <div className="branding-preview-subtitle">Generated by ReportForge</div>
              </div>
            </div>
            <div className="branding-preview-body">
              <div className="branding-preview-line" style={{ width: "70%" }} />
              <div className="branding-preview-line" style={{ width: "45%" }} />
              <span className="branding-preview-badge" style={{ background: secondaryColor }}>
                Status: Active
              </span>
              <a className="branding-preview-link" style={{ color: accentColor }} onClick={(e) => e.preventDefault()} href="#preview">
                View full statement →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
